import {
  TokenManager,
  authenticatedFetch,
  supabase,
} from "@/supabase/supabaseClient";
import type { BasemapConfig } from "@/types/layers";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

export interface BasemapFormData {
  key: string;
  label: string;
  type: "raster" | "vector";
  style_url: string;
  attribution?: string;
  is_visible?: boolean;
  display_order?: number;
}

export class BasemapService {
  /**
   * Fetch all basemaps
   */
  static async getAllBasemaps(tenantId: string): Promise<BasemapConfig[]> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/basemaps?select=*&or=(tenant_id.eq.${tenantId},tenant_id.is.null)&order=display_order.asc.nullslast,created_at.asc`
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error fetching basemaps:", error);
        throw new Error(`Failed to fetch basemaps: ${response.statusText}`);
      }

      const data = await response.json();

      // Transform database format to BasemapConfig format
      return data.map(
        (item: {
          key: string;
          label: string;
          type: "raster" | "vector";
          style_url: string;
          attribution: string | null;
          is_visible: boolean;
          display_order: number | null;
        }) => ({
          key: item.key,
          label: item.label,
          type: item.type,
          styleUrl: item.style_url,
          attribution: item.attribution || "",
          isVisible: item.is_visible ?? true,
          displayOrder: item.display_order ?? undefined,
        })
      ) as BasemapConfig[];
    } catch (error) {
      console.error("Error in getAllBasemaps:", error);
      throw error;
    }
  }

  /**
   * Get basemap by key
   */
  static async getBasemapByKey(
    key: string,
    tenantId: string
  ): Promise<BasemapConfig | null> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/basemaps?select=*&key=eq.${key}&or=(tenant_id.eq.${tenantId},tenant_id.is.null)`
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error fetching basemap by key:", error);
        return null;
      }

      const data = await response.json();
      if (!data || data.length === 0) return null;

      const item = data[0];
      return {
        key: item.key,
        label: item.label,
        type: item.type,
        styleUrl: item.style_url,
        attribution: item.attribution || "",
        isVisible: item.is_visible ?? true,
        displayOrder: item.display_order,
      };
    } catch (error) {
      console.error("Error in getBasemapByKey:", error);
      return null;
    }
  }

  /**
   * Create a new basemap
   */
  static async createBasemap(
    basemapData: BasemapFormData,
    tenantId: string
  ): Promise<void> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/basemaps`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Prefer: "return=minimal",
          },
          body: JSON.stringify({ ...basemapData, tenant_id: tenantId }),
        }
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error creating basemap:", error);
        throw new Error(`Failed to create basemap: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error in createBasemap:", error);
      throw error;
    }
  }

  /**
   * Update an existing basemap (uses RPC/POST to avoid CORS PATCH restrictions)
   */
  static async updateBasemap(
    key: string,
    basemapData: Partial<BasemapFormData>,
    tenantId: string
  ): Promise<void> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        throw new Error("Authentication token not available");
      }

      const { error } = await supabase.rpc("update_basemap", {
        p_tenant_id: tenantId,
        p_key: key,
        p_updates: basemapData,
      });

      if (error) {
        console.error("[BasemapService] Update failed:", error.message);
        throw new Error(`Failed to update basemap: ${error.message}`);
      }
    } catch (error) {
      console.error("[BasemapService] Error in updateBasemap:", error);
      throw error;
    }
  }

  /**
   * Delete a basemap
   */
  static async deleteBasemap(key: string, tenantId: string): Promise<void> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        throw new Error("Authentication token not available");
      }

      const { error } = await supabase.rpc("delete_basemap", {
        p_tenant_id: tenantId,
        p_key: key,
      });

      if (error) {
        console.error("Error deleting basemap:", error.message);
        throw new Error(`Failed to delete basemap: ${error.message}`);
      }
    } catch (error) {
      console.error("Error in deleteBasemap:", error);
      throw error;
    }
  }

  /**
   * Update basemap visibility
   */
  static async updateBasemapVisibility(
    key: string,
    isVisible: boolean,
    tenantId: string
  ): Promise<void> {
    await this.updateBasemap(key, { is_visible: isVisible }, tenantId);
  }

  /**
   * Reorder basemaps (uses RPC/POST to avoid CORS PATCH restrictions)
   */
  static async reorderBasemaps(
    updates: Array<{ key: string; display_order: number }>,
    tenantId: string
  ): Promise<void> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        throw new Error("Authentication token not available");
      }

      const { error } = await supabase.rpc("reorder_basemaps", {
        p_tenant_id: tenantId,
        p_updates: updates,
      });

      if (error) {
        console.error("[BasemapService] Reorder failed:", error.message);
        throw new Error(`Failed to reorder basemaps: ${error.message}`);
      }
    } catch (error) {
      console.error("[BasemapService] Error in reorderBasemaps:", error);
      throw error;
    }
  }
}
